import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackSaleEdited } from '../services/analytics';
import { Toast, Field, PageHeader, LoadingSpinner } from './v2-utils';
import '../styles/v2.css';

const today = () => new Date().toISOString().split('T')[0];

export default function EditSaleV2() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [products, setProducts]         = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [unitTypes, setUnitTypes]       = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [retailPrice, setRetailPrice]   = useState('');
  const [quantity, setQuantity]         = useState('');
  const [saleDate, setSaleDate]         = useState(today());
  const [dateWarning, setDateWarning]   = useState('');
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [toast, setToast]               = useState(null);
  const [originalSale, setOriginalSale] = useState(null);

  /* load products + sale data */
  useEffect(() => {
    Promise.all([
      axiosInstance.get('/products'),
      axiosInstance.get(`/sales/${id}`),
    ]).then(async ([prodsRes, saleRes]) => {
      setProducts(prodsRes.data || []);
      const sale = saleRes.data;
      setOriginalSale(sale);
      setSaleDate(sale.sale_date ? sale.sale_date.split('T')[0] : today());
      setRetailPrice(String(sale.retail_price || ''));
      setQuantity(String(sale.quantity || ''));

      /* find matching product */
      const productId = sale.product_id;
      if (productId) {
        setSelectedProductId(String(productId));
        try {
          const unitsRes = await axiosInstance.get(`/units/product/${productId}`);
          const selling = (unitsRes.data || []).filter(u => u.unit_category === 'selling');
          setUnitTypes(selling);
          const matchedUnit = selling.find(u => u.unit_id === sale.unit_id || u.unit_type === sale.unit_type);
          if (matchedUnit) setSelectedUnitId(String(matchedUnit.unit_id));
          else if (selling.length > 0) {
            setSelectedUnitId(String(selling[0].unit_id));
            setToast({ message: 'Original unit not found — please re-select a unit.', severity: 'warning' });
          }
        } catch {}
      }
    }).catch(() => setToast({ message: 'Failed to load sale data.', severity: 'error' }))
    .finally(() => setLoading(false));
  }, [id]);

  /* product change → reload units */
  const handleProductChange = async (productId) => {
    setSelectedProductId(productId);
    setSelectedUnitId('');
    setUnitTypes([]);
    if (!productId) return;
    try {
      const res = await axiosInstance.get(`/units/product/${productId}`);
      setUnitTypes((res.data || []).filter(u => u.unit_category === 'selling'));
    } catch {}
  };

  const selectedUnit = unitTypes.find(u => String(u.unit_id) === String(selectedUnitId));
  const selectedProduct = products.find(p => String(p.product_id) === String(selectedProductId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductId) { setToast({ message: 'Please select a product.', severity: 'error' }); return; }
    if (!selectedUnit) { setToast({ message: 'Please select a unit type.', severity: 'error' }); return; }
    setSaving(true);
    try {
      await axiosInstance.put(`/sales/${id}`, {
        product_name: selectedProduct?.product_name || originalSale?.product_name,
        variety:      selectedProduct?.variety      || originalSale?.variety || '',
        brand:        selectedProduct?.brand        || originalSale?.brand   || '',
        retail_price: retailPrice,
        quantity,
        sale_date: saleDate,
        unit_id: selectedUnitId,
        unit_category: selectedUnit.unit_category,
      });
      trackSaleEdited(id);
      setToast({ message: 'Sale updated successfully!', severity: 'success' });
      setTimeout(() => navigate('/new/dashboard/sales/view'), 1000);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update sale.', severity: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingSpinner message="Loading sale…" />;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <PageHeader title="Edit Sale" onBack={() => navigate('/new/dashboard/sales/view')} />
      <form onSubmit={handleSubmit}>
        <div className="v2-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Sale Details</p>

          <Field label="Product *">
            <div style={{ position: 'relative' }}>
              <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                value={selectedProductId} onChange={e => handleProductChange(e.target.value)} required>
                <option value="">Select product…</option>
                {products.map(p => (
                  <option key={p.product_id} value={String(p.product_id)}>
                    {p.product_name}{p.brand ? ` (${p.brand})` : ''}{p.variety ? ` - ${p.variety}` : ''}
                  </option>
                ))}
              </select>
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </div>
          </Field>

          <Field label="Selling Unit *">
            <div style={{ position: 'relative' }}>
              <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                value={selectedUnitId} onChange={e => setSelectedUnitId(e.target.value)} required disabled={!unitTypes.length}>
                <option value="">Select unit…</option>
                {unitTypes.map(u => <option key={u.unit_id} value={String(u.unit_id)}>{u.unit_type}</option>)}
              </select>
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </div>
          </Field>

          <div className="v2-sale-price-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label={selectedUnit ? `Price per ${selectedUnit.unit_type} (K) *` : 'Retail Price (K) *'}>
              <input className="v2-input" type="number" value={retailPrice} onChange={e => setRetailPrice(e.target.value)} min="0" step="0.01" required />
            </Field>
            <Field label="Quantity *">
              <input className="v2-input" type="number" value={quantity} onChange={e => setQuantity(e.target.value)} min="0" step="0.01" required />
            </Field>
          </div>

          <Field label="Sale Date *">
            <input className="v2-input" type="date" value={saleDate}
              onChange={e => { setSaleDate(e.target.value); setDateWarning(e.target.value > today() ? 'Note: Future date entered.' : ''); }} required />
            {dateWarning && <p style={{ fontSize: 11, color: '#f59e0b', margin: '4px 0 0', fontWeight: 500 }}>{dateWarning}</p>}
          </Field>

          <button type="submit" className="v2-btn-submit" disabled={saving} style={{ fontSize: 15 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
